import {
    createContext,
    useContext,
    useState,
    useEffect,
    type ReactNode,
} from 'react';

interface SessionExpiredContextType {
    isSessionExpired: boolean;
    setSessionExpired: (expired: boolean) => void;
}

const SessionExpiredContext = createContext<SessionExpiredContextType | null>(null);

interface SessionExpiredProviderProps {
    children: ReactNode;
}

export function SessionExpiredProvider({ children }: SessionExpiredProviderProps) {
    const [isSessionExpired, setIsSessionExpired] = useState(false);

    // Listen for the auth:session-expired event dispatched by the API client
    useEffect(() => {
        const handleSessionExpired = () => {
            setIsSessionExpired(true);
        };

        window.addEventListener('auth:session-expired', handleSessionExpired);
        return () => {
            window.removeEventListener('auth:session-expired', handleSessionExpired);
        };
    }, []);

    const setSessionExpired = (expired: boolean) => {
        setIsSessionExpired(expired);
    };

    return (
        <SessionExpiredContext.Provider value={{ isSessionExpired, setSessionExpired }}>
            {children}
        </SessionExpiredContext.Provider>
    );
}

export function useSessionExpired(): SessionExpiredContextType {
    const context = useContext(SessionExpiredContext);
    if (!context) {
        throw new Error('useSessionExpired must be used within a SessionExpiredProvider');
    }
    return context;
}
